/**
 * MapZoneSuggestions — hunting-zone results for the map search bar.
 *
 * Rendered by `MapSearchBar` into `MapboxSearch`'s results slot, ABOVE the
 * address results and under its own heading: a zone and an address are
 * different kinds of destination, and blending them into one list would put
 * them in competition for the same row. Picking one only reports the zone id —
 * selection is a URL concern (`?zone=<id>`), and the camera work that follows
 * belongs to `MapZoneFocus` / `MapCamera`.
 *
 * The suggestion list is the zone-name catalogue from `GET /zones/filters`
 * filtered in the browser, so typing issues no request at all. The backend is
 * touched once, on a pick, to resolve the picked name to an id.
 *
 * Read-only by spec (FR-013): a row is a way to open a zone, never to edit one.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Shapes } from 'lucide-react';
import { CommandGroup, CommandItem } from '@/components/atoms/command';
import { useZoneFilterOptionsQuery, zonesKeyFactory } from '@/features/zones/services/zones.api';
import { isZoneCodePrefix, stripWhitespace } from '@/features/zones/utils/isZoneCodePrefix';
import { notifyError } from '@/libs/bugsnag';

/** Zone suggestions offered per search. Kept short so the address results below stay visible. */
const ZONE_SUGGESTION_LIMIT = 3;

interface MapZoneSuggestionsProps {
  /** The live search text, straight from the input. */
  query: string;
  /** Called with the picked zone's id. */
  onSelectZone: (zoneId: string) => void;
}

export function MapZoneSuggestions({ query, onSelectZone }: MapZoneSuggestionsProps) {
  const queryClient = useQueryClient();
  const { data } = useZoneFilterOptionsQuery();

  const trimmed = query.trim();
  // Display gate: an address never brings up zone rows.
  if (!isZoneCodePrefix(trimmed)) {
    return null;
  }

  // Whitespace-insensitive on both sides, or `AT-H` would miss `AT - H - 4`.
  const needle = stripWhitespace(trimmed).toLowerCase();
  const names = (data?.names ?? [])
    .filter((name) => stripWhitespace(name).toLowerCase().includes(needle))
    .slice(0, ZONE_SUGGESTION_LIMIT);

  if (names.length === 0) {
    return null;
  }

  // The catalogue carries names only, so the id comes from a one-shot list
  // request on pick. `api.zones` does not constrain `name`, so a duplicate name
  // resolves to whichever row comes first.
  async function selectByName(name: string): Promise<void> {
    try {
      const page = await queryClient.fetchQuery(zonesKeyFactory.list({ names: [name], limit: 1 }));
      const zone = page.zones.at(0);
      if (zone == null) {
        return;
      }
      onSelectZone(zone.id);
    } catch (error: unknown) {
      notifyError(error, { context: 'zones:map:search:resolveName' });
    }
  }

  return (
    <CommandGroup className="pb-1">
      <span className="text-sm text-primary-500">Hunting zones</span>
      {names.map((name) => (
        <CommandItem
          key={name}
          value={`zone-${name}`}
          onSelect={() => void selectByName(name)}
          className="cursor-pointer hover:!bg-brand-150 data-[selected=true]:bg-brand-150"
        >
          <Shapes className="size-4 text-muted-foreground" />
          <div className="text-sm font-medium">{name}</div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}
